/* Marketplace collection: live filtering, sorting and "Load more" through the
   Section Rendering API. Without this script every control still works as a
   normal link or GET form. */
class MkCollection extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.cache = new Map();
    this.controller = null;
    this.debounceTimer = null;
    this.drawerQuery = window.matchMedia('(max-width: 989px)');

    this.onInput = this.onInput.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onPopState = () => this.load(window.location.search.slice(1), { push: false });

    this.addEventListener('input', this.onInput);
    this.addEventListener('click', this.onClick);
    this.addEventListener('submit', this.onSubmit);
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);
    window.addEventListener('popstate', this.onPopState);

    this.syncDensity();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('popstate', this.onPopState);
    clearTimeout(this.debounceTimer);
    if (this.controller) this.controller.abort();
  }

  get facets() {
    return this.querySelector('#MkFacets');
  }

  get toggle() {
    return this.querySelector('[data-mk-filter-toggle]');
  }

  /* ── Events ────────────────────────────────────────────────────── */
  onInput(event) {
    const field = event.target;
    if (!field.name) return;
    clearTimeout(this.debounceTimer);
    // typed values wait for a pause; checkboxes and the sort menu apply at once
    const wait = field.type === 'number' || field.type === 'text' ? 500 : 0;
    this.debounceTimer = setTimeout(() => this.load(this.serialize()), wait);
  }

  onSubmit(event) {
    event.preventDefault();
    clearTimeout(this.debounceTimer);
    this.load(this.serialize());
  }

  onClick(event) {
    if (event.target.closest('[data-mk-filter-toggle]')) {
      this.classList.contains('is-filters-open') ? this.closeFilters() : this.openFilters();
      return;
    }
    if (event.target.closest('[data-mk-filter-close]')) this.closeFilters();

    const density = event.target.closest('[data-mk-density]');
    if (density) {
      this.setDensity(density.dataset.mkDensity);
      return;
    }

    if (event.metaKey || event.ctrlKey || event.shiftKey) return;

    const more = event.target.closest('a[data-mk-more]');
    if (more) {
      event.preventDefault();
      this.loadMore(more);
      return;
    }

    const link = event.target.closest('a[data-mk-nav]');
    if (!link) return;
    event.preventDefault();
    this.load(new URL(link.href).search.slice(1), { scroll: link.hasAttribute('data-mk-scroll') });
  }

  // Dropdown pills in the desktop bar close when the click lands elsewhere.
  onDocumentClick(event) {
    if (this.drawerQuery.matches) return;
    this.querySelectorAll('.mk-facet[open]').forEach((facet) => {
      if (!facet.contains(event.target)) facet.open = false;
    });
  }

  onKeydown(event) {
    if (event.code !== 'Escape') return;
    if (this.classList.contains('is-filters-open')) {
      this.closeFilters();
      return;
    }
    if (this.drawerQuery.matches) return;
    const open = this.querySelector('.mk-facet[open]');
    if (!open) return;
    open.open = false;
    open.querySelector('summary').focus();
  }

  /* ── Grid density (4 or 6 columns from 1200px) ─────────────────── */
  setDensity(value) {
    if (value !== 'dense' && value !== 'spacious') return;
    this.classList.remove('mk-grid--dense', 'mk-grid--spacious');
    this.classList.add(`mk-grid--${value}`);
    try {
      localStorage.setItem('mk-grid-density', value);
    } catch (error) {
      // storage can be blocked; the choice then lasts for this page only
    }
    this.syncDensity();
  }

  // The class is set before paint by the inline script; mirror it on the buttons.
  syncDensity() {
    this.querySelectorAll('[data-mk-density]').forEach((button) => {
      const on = this.classList.contains(`mk-grid--${button.dataset.mkDensity}`);
      button.setAttribute('aria-pressed', String(on));
    });
  }

  /* ── Filter drawer (below 990px) ───────────────────────────────── */
  openFilters() {
    if (!this.facets) return;
    this.classList.add('is-filters-open');
    document.body.classList.add('overflow-hidden');
    this.toggle.setAttribute('aria-expanded', 'true');
    const close = this.facets.querySelector('.mk-facets__close');
    if (typeof trapFocus === 'function') trapFocus(this.facets, close);
    else if (close) close.focus();
  }

  closeFilters() {
    if (!this.classList.contains('is-filters-open')) return;
    this.classList.remove('is-filters-open');
    document.body.classList.remove('overflow-hidden');
    this.toggle.setAttribute('aria-expanded', 'false');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.toggle);
    else this.toggle.focus();
  }

  /* ── Data ──────────────────────────────────────────────────────── */
  serialize() {
    const params = new URLSearchParams();
    const form = this.querySelector('[data-mk-facets-form]');
    if (form) {
      new FormData(form).forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });
    }
    const sort = this.querySelector('[data-mk-sort]');
    if (sort && !params.has('sort_by')) params.set('sort_by', sort.value);
    return params.toString();
  }

  async fetchSection(search, signal) {
    const cached = this.cache.get(search);
    if (cached) return cached;
    const query = `section_id=${this.sectionId}${search ? `&${search}` : ''}`;
    const response = await fetch(`${window.location.pathname}?${query}`, { signal });
    if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
    const html = await response.text();
    this.cache.set(search, html);
    return html;
  }

  parse(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  /* Replace the grid for a new filter, sort or page state. */
  async load(search, { push = true, scroll = false } = {}) {
    const results = this.querySelector('[data-mk-region="results"]');
    const path = window.location.pathname;
    const pageUrl = search ? `${path}?${search}` : path;

    if (this.controller) this.controller.abort();
    this.controller = new AbortController();

    // hold the current height so nothing below the grid moves while loading
    if (results) results.style.minHeight = `${results.offsetHeight}px`;
    this.setAttribute('aria-busy', 'true');

    try {
      const html = await this.fetchSection(search, this.controller.signal);
      this.render(this.parse(html));
      if (push) window.history.pushState({}, '', pageUrl);
      if (scroll && results) {
        results.scrollIntoView({ block: 'start' });
        results.focus({ preventScroll: true });
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.assign(pageUrl);
      return;
    }

    if (results) results.style.minHeight = '';
    this.removeAttribute('aria-busy');
  }

  /* Append the next page to the grid. The URL is left alone, so filters and
     sorting keep working from the first page. */
  async loadMore(link) {
    if (link.getAttribute('aria-disabled') === 'true') return;
    link.setAttribute('aria-disabled', 'true');
    link.classList.add('is-loading');

    try {
      const next = this.parse(await this.fetchSection(new URL(link.href).search.slice(1)));
      const grid = this.querySelector('[data-mk-grid]');
      const more = this.querySelector('[data-mk-more-wrap]');
      const freshMore = next.querySelector('[data-mk-more-wrap]');
      const items = Array.from(next.querySelectorAll('[data-mk-grid] > li'));
      if (!grid || !more || !freshMore || items.length === 0) throw new Error('Unexpected section markup');

      grid.append(...items);

      const first = more.dataset.first;
      more.innerHTML = freshMore.innerHTML;
      more.dataset.last = freshMore.dataset.last;
      more.dataset.total = freshMore.dataset.total;

      const template = first === '1' ? more.dataset.textCount : more.dataset.textRange;
      const text = more.querySelector('[data-mk-more-text]');
      if (text && template) {
        text.textContent = template
          .replace('[first]', first)
          .replace('[last]', more.dataset.last)
          .replace('[total]', more.dataset.total);
      }

      const firstLink = items[0].querySelector('a');
      if (firstLink) firstLink.focus({ preventScroll: true });
    } catch (error) {
      window.location.assign(link.href);
    }
  }

  render(next) {
    this.querySelectorAll('[data-mk-region]').forEach((region) => {
      const fresh = next.querySelector(`[data-mk-region="${region.dataset.mkRegion}"]`);
      if (fresh) region.innerHTML = fresh.innerHTML;
    });

    const sort = this.querySelector('[data-mk-sort]');
    const freshSort = next.querySelector('[data-mk-sort]');
    if (sort && freshSort && document.activeElement !== sort) sort.value = freshSort.value;

    this.renderFacets(next);
  }

  // Update each filter group in place so open/closed state survives, and
  // leave the group the shopper is typing or tabbing in untouched.
  renderFacets(next) {
    const list = this.querySelector('[data-mk-facets-list]');
    const freshList = next.querySelector('[data-mk-facets-list]');
    if (!list || !freshList) return;

    const keys = (root) => Array.from(root.querySelectorAll('[data-mk-filter]'), (group) => group.dataset.mkFilter);
    if (keys(list).join('|') !== keys(freshList).join('|')) {
      list.innerHTML = freshList.innerHTML;
      return;
    }

    const groups = list.querySelectorAll('[data-mk-filter]');
    freshList.querySelectorAll('[data-mk-filter]').forEach((fresh, index) => {
      const group = groups[index];
      const count = group.querySelector('[data-mk-filter-count]');
      const freshCount = fresh.querySelector('[data-mk-filter-count]');
      if (count && freshCount) count.innerHTML = freshCount.innerHTML;

      const body = group.querySelector('[data-mk-filter-body]');
      if (body.contains(document.activeElement)) return;
      body.innerHTML = fresh.querySelector('[data-mk-filter-body]').innerHTML;
    });
  }
}

if (!customElements.get('mk-collection')) customElements.define('mk-collection', MkCollection);
